// Archivio locale dell'app auditor (IndexedDB) e coda di sincronizzazione.
// Cosa sta qui: la checklist scaricata (per lavorare senza rete), le risposte non ancora inviate, le foto in attesa.
// Regola: nulla si perde. Una risposta o una foto esce dalla coda SOLO quando il server ha risposto "ok".
import { API_BASE } from "@/api/client";

const DB_NAME = "dvr-auditor";
const DB_VERSION = 1;
const STORES = ["audits", "queue", "blobs"];
let dbPromise = null;

export function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("IndexedDB non disponibile")); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("audits")) db.createObjectStore("audits", { keyPath: "id" });
      if (!db.objectStoreNames.contains("queue")) db.createObjectStore("queue", { keyPath: "qid", autoIncrement: true });
      if (!db.objectStoreNames.contains("blobs")) db.createObjectStore("blobs", { keyPath: "uuid" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(store, mode, fn) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const out = fn(t.objectStore(store));
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : undefined);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

const reqP = (r) => new Promise((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });

export async function putAudit(rec) { await tx("audits", "readwrite", (s) => s.put(rec)); }
export async function getAudit(id) { const db = await openDb(); return reqP(db.transaction("audits").objectStore("audits").get(id)); }
export async function listAudits() { const db = await openDb(); return reqP(db.transaction("audits").objectStore("audits").getAll()); }
export async function queueAll() { const db = await openDb(); return reqP(db.transaction("queue").objectStore("queue").getAll()); }
export async function queueAdd(op) { await tx("queue", "readwrite", (s) => s.add({ ...op, at: new Date().toISOString() })); }
export async function queueDelete(qid) { await tx("queue", "readwrite", (s) => s.delete(qid)); }
export async function queuePut(op) { await tx("queue", "readwrite", (s) => s.put(op)); }
export async function blobPut(uuid, blob) { await tx("blobs", "readwrite", (s) => s.put({ uuid, blob })); }
export async function blobGet(uuid) { const db = await openDb(); const r = await reqP(db.transaction("blobs").objectStore("blobs").get(uuid)); return r ? r.blob : null; }
export async function blobDelete(uuid) { await tx("blobs", "readwrite", (s) => s.delete(uuid)); }
export async function clearAll() { for (const s of STORES) await tx(s, "readwrite", (st) => st.clear()); }

// L'estensione deve corrispondere al contenuto vero del file: il server controlla i primi byte.
export function extFor(blob) {
  const t = (blob && blob.type) || "";
  if (t.includes("png")) return "png";
  if (t.includes("webp")) return "webp";
  if (t.includes("heic") || t.includes("heif")) return "heic";
  if (t.includes("pdf")) return "pdf";
  return "jpg";
}

export const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export function deviceId() {
  let d = localStorage.getItem("dvr_device");
  if (!d) { d = `dev-${newId().slice(0, 8)}`; localStorage.setItem("dvr_device", d); }
  return d;
}

// ------------------------------------------------------------------ coda
// op: { type: "answers", auditId, items: {key: item} }  (coalescenza: l'ultima risposta per chiave)
//     { type: "evidence", auditId, uuid, meta }          (il file e' in "blobs")
export async function enqueueAnswer(auditId, key, item) {
  const all = await queueAll();
  const open = all.find((o) => o.type === "answers" && o.auditId === auditId);
  if (open) { open.items[key] = item; await queuePut(open); } else { await queueAdd({ type: "answers", auditId, items: { [key]: item } }); }
}

export async function enqueueEvidence(auditId, uuid, blob, meta) {
  await blobPut(uuid, blob);
  await queueAdd({ type: "evidence", auditId, uuid, meta });
}

export async function failedCount() {
  return (await queueAll()).filter((o) => o.failed).length;
}

export async function pendingCount(auditId) {
  const all = await queueAll();
  return all.filter((o) => !o.failed && (!auditId || o.auditId === auditId)).reduce((n, o) => n + (o.type === "answers" ? Object.keys(o.items).length : 1), 0);
}

function authHeaders() {
  const token = localStorage.getItem("dvr_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Svuota la coda in ordine: prima le foto (per avere l'id del server), poi le risposte con gli id corretti.
// Ritorna { ok, sent, conflicts, rejected, ignored, error }.
export async function flushQueue(onProgress) {
  const result = { ok: true, sent: 0, conflicts: [], rejected: [], ignored: [], error: null, rev: {} };
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { ...result, ok: false, error: "offline" };
  const ops = (await queueAll()).filter((o) => !o.failed).sort((a, b) => (a.type === "evidence" ? -1 : 1) - (b.type === "evidence" ? -1 : 1) || a.qid - b.qid);
  const evMap = JSON.parse(localStorage.getItem("dvr_evmap") || "{}");
  for (const op of ops) {
    try {
      if (op.type === "evidence") {
        const blob = await blobGet(op.uuid);
        if (!blob) { await queueDelete(op.qid); continue; }
        const fd = new FormData();
        fd.append("file", blob, op.meta.filename || `${op.uuid}.jpg`);
        fd.append("kind", op.meta.kind || "photo");
        fd.append("client_uuid", op.uuid);
        ["question_code", "task_code", "equipment_code", "taken_at", "signer"].forEach((k) => { if (op.meta[k]) fd.append(k, op.meta[k]); });
        if (op.meta.geo) { fd.append("lat", op.meta.geo.lat); fd.append("lon", op.meta.geo.lon); if (op.meta.geo.accuracy != null) fd.append("accuracy", op.meta.geo.accuracy); }
        const res = await fetch(`${API_BASE}/auditor/audits/${op.auditId}/evidence`, { method: "POST", headers: authHeaders(), body: fd });
        if (res.status === 409 || res.status === 404) { await queueDelete(op.qid); await blobDelete(op.uuid); result.rejected.push({ key: op.uuid, why: "audit non modificabile" }); continue; }
        if ([400, 413, 415, 422].includes(res.status)) {
          // rifiuto definitivo (file non valido): lo segno e vado avanti, senza perderlo e senza bloccare il resto
          await queuePut({ ...op, failed: `rifiutato dal server (${res.status})` });
          result.rejected.push({ key: op.uuid, why: `foto non accettata (${res.status})` });
          continue;
        }
        if (!res.ok) throw new Error(`upload ${res.status}`);
        const data = await res.json();
        evMap[op.uuid] = data.id;
        localStorage.setItem("dvr_evmap", JSON.stringify(evMap));
        await queueDelete(op.qid);
        await blobDelete(op.uuid);
        result.sent += 1;
      } else if (op.type === "answers") {
        const items = Object.entries(op.items).map(([key, it]) => ({ ...it, key, evidence_ids: (it.evidence_ids || []).map((e) => evMap[e] || e) }));
        const res = await fetch(`${API_BASE}/auditor/audits/${op.auditId}/answers`, {
          method: "PUT", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ answers: items }) });
        if (res.status === 409) { await queueDelete(op.qid); result.rejected.push({ key: op.auditId, why: "audit in sola lettura" }); continue; }
        if (!res.ok) throw new Error(`sync ${res.status}`);
        const data = await res.json();
        await queueDelete(op.qid);
        result.sent += items.length;
        result.conflicts.push(...(data.conflicts || []));
        result.rejected.push(...(data.rejected || []));
        result.ignored.push(...(data.ignored_inactive || []));
        result.rev[op.auditId] = data.rev;
      }
      if (onProgress) onProgress(result);
    } catch (e) {
      return { ...result, ok: false, error: e.message || "errore di rete" };
    }
  }
  return result;
}

// ------------------------------------------------------------------ foto: compressione lato client
export async function compressImage(file, maxSide = 1600, quality = 0.72) {
  if (!file.type.startsWith("image/") || typeof createImageBitmap === "undefined") return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

export function currentPosition(timeoutMs = 4000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null), { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60000 });
  });
}
